package com.sddamico.draftmachine.ds

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier

/**
 * Draft Machine — motion tokens. Full Kotlin translation of the motion section of
 * `tokens/effects.css`. Weighty but soft: machinery easing to rest.
 *
 * The `dm-spin` / `dm-spin-rev` keyframes and the `prefers-reduced-motion` reset are
 * injected globally as a <style> block in build.gradle.kts (see [Ds] head wiring), so the
 * [spin] helper below only needs to reference them by name. Screen transitions and the
 * enter/exit keyframes live in `ds/motion/` and read these values.
 */
object DmMotion {
  // Easings (as CSS timing-function strings, for use in `transition` / `animation`)
  const val easeCog = "cubic-bezier(.34, 1.32, .52, 1)" // slight geared overshoot — toggles/steppers
  const val easeOut = "cubic-bezier(.22, .61, .36, 1)" // most transitions
  const val easeInout = "cubic-bezier(.65, .05, .36, 1)"
  const val easeSnap = "cubic-bezier(.16, 1, .3, 1)" // arrivals: most of the travel in the first third
  const val easeExit = "cubic-bezier(.55, 0, .8, .2)" // departures: leave quickly, no lingering
  // A damped spring sampled into `linear()` — a single small overshoot that settles, for pops.
  const val easeSpring =
    "linear(0, .22 6%, .6 14%, .92 24%, 1.06 32%, 1.09 38%, 1.06 46%, 1.01 58%, .99 70%, 1)"

  // Durations (as CSS time strings)
  const val durFast = "120ms"
  const val durBase = "200ms"
  const val durSlow = "360ms"
  const val durGear = "9s" // decorative gear rotation

  // Screen transitions, in milliseconds so the transition engine can schedule against them.
  const val pageEnterMs = 340
  const val pageExitMs = 150
  const val sheetEnterMs = 420
  const val sheetExitMs = 260
  const val overlayExitMs = 160
}

/** Apply an arbitrary CSS `transition` value, e.g. `"filter 120ms cubic-bezier(...)"`. */
fun Modifier.transitionRaw(value: String) = styleModifier { property("transition", value) }

/**
 * Continuously rotate the element (the ambient machinery motif). References the global
 * `dm-spin` / `dm-spin-rev` keyframes; disabled automatically under `prefers-reduced-motion`.
 */
fun Modifier.spin(duration: String = DmMotion.durGear, reverse: Boolean = false) = styleModifier {
  property("animation", "${if (reverse) "dm-spin-rev" else "dm-spin"} $duration linear infinite")
}
